import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { I18n } from "@aws-amplify/core";
import dayjs from "dayjs";
import { post } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "./APIStatusDialog";

function AddAlarmTypeDialog({ open, onClose, onSave, languages }) {
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  useEffect(() => {
    if (open) {
      // Reset fields when dialog opens
      setSeverity("");
      setSeverityError(false);
      setLanguageFields(
        languages.reduce((fields, language) => {
          fields[language] = "";
          return fields;
        }, {})
      );
      setLanguageErrors({});
    }
  }, [open, languages]);

  const AddNewType = async () => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Adding alarm Type"),
      });
      if (!severity || !languageFields.ENGLISH) {
        throw new Error(
          I18n.get("Severity and English description are required")
        ); // Traduzione
      }

      const requestBody = {};
      Object.keys(languageFields).forEach((language) => {
        if (languageFields[language].trim()) {
          requestBody[language] = languageFields[language].trim();
        }
      });

      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/type/new",
        options: {
          body: requestBody,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            alarm_severity: severity,
            alarm_name: languageFields.ENGLISH,
          },
        },
      });

      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Alarm Type Added"), // Traduzione
        });
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Operation Failed"), // Traduzione
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
        onClose();
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Operation Failed"), // Traduzione
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
        onClose();
      }, 2000);
    }
  };

  const formatLanguageName = (language) => {
    return I18n.get(
      language.charAt(0).toUpperCase() + language.slice(1).toLowerCase()
    ); // Traduzione dei nomi delle lingue
  };

  const [severity, setSeverity] = useState("");
  const [severityError, setSeverityError] = useState(false);
  const [languageFields, setLanguageFields] = useState(() => {
    const initialFields = {};
    languages.forEach((language) => {
      initialFields[language] = "";
    });
    return initialFields;
  });
  const [languageErrors, setLanguageErrors] = useState({});

  const handleSave = async () => {
    const englishValue = languageFields.ENGLISH || "";
    const englishError = englishValue === "";

    if (severity === "" || englishError) {
      setSeverityError(severity === "");
      setLanguageErrors({
        ...languageErrors,
        ENGLISH: englishError,
      });
    } else {
      await AddNewType(); // Call AddNewType to save the new alarm type
      onSave({ severity, ...languageFields }); // Pass the data back to the parent component
    }
  };

  const handleChange = (event) => {
    setSeverity(event.target.value);
    if (event.target.value !== "") {
      setSeverityError(false);
    }
  };

  const handleLanguageChange = (language, event) => {
    const value = event.target.value;
    setLanguageFields((prevFields) => ({
      ...prevFields,
      [language]: value,
    }));

    if (language === "ENGLISH" && value !== "") {
      setLanguageErrors((prevErrors) => ({
        ...prevErrors,
        ENGLISH: false,
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{I18n.get("Add New Alarm Type")}</DialogTitle>{" "}
      {/* Traduzione */}
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <TextField
            autoFocus
            required
            fullWidth
            label={I18n.get("Severity")} // Traduzione
            type="number"
            inputProps={{
              min: 0,
              max: 10,
              onKeyDown: (e) => {
                if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                  e.preventDefault();
                }
              },
            }}
            value={severity}
            onChange={handleChange}
            error={severityError}
            helperText={severityError ? I18n.get("Severity is required") : ""} // Traduzione
            sx={{ mb: 2 }}
          />
          {languages.map((language) => (
            <TextField
              key={language}
              required={language === "ENGLISH"}
              fullWidth
              multiline
              label={formatLanguageName(language)} // Traduzione dei nomi delle lingue
              value={languageFields[language]}
              onChange={(event) => handleLanguageChange(language, event)}
              error={language === "ENGLISH" && languageErrors.ENGLISH}
              helperText={
                language === "ENGLISH" && languageErrors.ENGLISH
                  ? I18n.get("English text is required") // Traduzione
                  : ""
              }
              sx={{ mb: 2 }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {I18n.get("Cancel")} {/* Traduzione */}
        </Button>
        <Button onClick={handleSave} color="primary">
          {I18n.get("Save")} {/* Traduzione */}
        </Button>
      </DialogActions>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
        onClose={() =>
          setApiStatus((prevState) => ({ ...prevState, open: false }))
        }
      />
    </Dialog>
  );
}

export default AddAlarmTypeDialog;
